import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';
import { garantirUsuario } from '@/lib/usuario';
import { guardarDestino } from '@/lib/destino';
import { gerarTicketIngresso, salvarOuCompartilhar } from '@/pages/carteira/utils';
import { gerarConvite } from '@/lib/conviteImagem';
import { mascaraCPF } from '@/pages/inscricao/utils';
import { solicitarAvisosAposSucesso } from '@/lib/avisosSucesso';
import { mensagemAmigavel } from '@/lib/mensagemAmigavel';
import CartaoIngresso from './components/CartaoIngresso';
import CaixaConta from './components/CaixaConta';
import { formatarDataHora, formatarDataWhats } from './utils';
import type { IngressoPedido, LinhaConta, Pix, StatusPedido } from './types';

const INTERVALO_POLLING_MS = 5000;
const TEMPO_PIX_MS = 30 * 60 * 1000;

// Total a exibir no topo do Pix: usa a linha em destaque do detalhe;
// sem detalhe, cai no total_reais formatado (função antiga ainda no ar).
function totalDaConta(detalhe: LinhaConta[] | undefined, totalReais: string): string | null {
  const linhaTotal = detalhe?.find((l) => l.destaque);
  if (linhaTotal?.valor) return linhaTotal.valor;
  if (!totalReais) return null;
  return Number(totalReais).toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  });
}

export default function PedidoPage() {
  const { pedidoId } = useParams<{ pedidoId: string }>();
  const navigate = useNavigate();
  const { user, pronto } = useAuth();
  const idNum = pedidoId ? Number(pedidoId) : null;

  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const [naoEncontrado, setNaoEncontrado] = useState(false);
  const [ingressos, setIngressos] = useState<IngressoPedido[]>([]);
  const [emailConta, setEmailConta] = useState('');
  const [emailDigitado, setEmailDigitado] = useState('');
  const [emailAberto, setEmailAberto] = useState(false);
  const [enviandoEmail, setEnviandoEmail] = useState(false);
  const [emailEnviadoPara, setEmailEnviadoPara] = useState<string | null>(null);
  const [emailErro, setEmailErro] = useState<string | null>(null);

  // ---- Pix ----
  const [pix, setPix] = useState<Pix | null>(null);
  const [pixErro, setPixErro] = useState<string | null>(null);
  const [gerandoPix, setGerandoPix] = useState(false);
  const [pixExpirado, setPixExpirado] = useState(false);
  const [pixExpiraEm, setPixExpiraEm] = useState<number | null>(null);
  const [copiado, setCopiado] = useState(false);
  const [contaPaga, setContaPaga] = useState<LinhaConta[]>([]);

  const avisosPedidosRef = useRef(false);

  const status: StatusPedido | null = ingressos.length > 0 ? ingressos[0].status_pedido : null;

  // Carga inicial.
  useEffect(() => {
    let ativo = true;

    async function carregar() {
      setLoading(true);
      setErro(null);
      setNaoEncontrado(false);
      try {
        if (!pronto) return;
        if (!user) {
          guardarDestino();
          navigate(`/entrar?voltar=${encodeURIComponent(`/pedido/${pedidoId}`)}`, {
            replace: true,
          });
          return;
        }

        await garantirUsuario();

        if (ativo) {
          setEmailConta(user.email ?? '');
          setEmailDigitado(user.email ?? '');
        }

        const { data: ingData, error: errIng } = await supabase
          .from('vw_meus_ingressos')
          .select('*')
          .eq('pedido_id', idNum);
        if (errIng) throw errIng;

        const lista = (ingData as IngressoPedido[]) || [];
        if (ativo) {
          setIngressos(lista);
          if (lista.length === 0) setNaoEncontrado(true);
        }
      } catch (e) {
        if (ativo) {
          setErro(mensagemAmigavel(e, 'Não foi possível carregar o pedido. Tente novamente.'));
        }
      } finally {
        if (ativo) setLoading(false);
      }
    }

    carregar();
    return () => {
      ativo = false;
    };
  }, [pedidoId, idNum, navigate, pronto, user]);

  // Gera (ou recupera) o Pix assim que o pedido estiver aguardando.
  const gerarPix = useCallback(async () => {
    setGerandoPix(true);
    setPixErro(null);
    setPixExpirado(false);
    setCopiado(false);
    try {
      const { data: pixData, error } = await supabase.functions.invoke('criar-pix-pedido', {
        body: { pedido_id: Number(pedidoId) },
      });
      if (error) {
        setPixErro(mensagemAmigavel(error, 'Não conseguimos gerar o Pix.'));
        return;
      }
      // O pedido já foi pago — recarrega para mostrar os ingressos.
      if (pixData?.ja_pago) {
        window.location.reload();
        return;
      }
      if (pixData?.ok === false) {
        setPixErro(
          mensagemAmigavel(pixData?.erro, 'Não conseguimos gerar o Pix. Tente de novo.'),
        );
        return;
      }
      setPix(pixData as Pix);
      const expMs = pixData?.expira_em ? new Date(pixData.expira_em).getTime() : NaN;
      setPixExpiraEm(Number.isFinite(expMs) ? expMs : Date.now() + TEMPO_PIX_MS);
    } catch (e) {
      setPixErro(mensagemAmigavel(e, 'Não conseguimos gerar o Pix. Tente de novo.'));
    } finally {
      setGerandoPix(false);
    }
  }, [pedidoId]);

  useEffect(() => {
    if (status === 'aguardando') {
      gerarPix();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  // Enquanto aguarda o Pix, consulta de novo a cada 5s.
  useEffect(() => {
    if (status !== 'aguardando') return;

    const intervalo = setInterval(async () => {
      try {
        const { data } = await supabase
          .from('vw_meus_ingressos')
          .select('*')
          .eq('pedido_id', idNum);
        const lista = (data as IngressoPedido[]) || [];
        if (lista.length > 0) setIngressos(lista);
      } catch {
        // silencioso: o próximo tick tenta de novo
      }
    }, INTERVALO_POLLING_MS);

    return () => clearInterval(intervalo);
  }, [status, idNum]);

  // Marca o Pix como expirado depois do prazo.
  useEffect(() => {
    if (status !== 'aguardando' || !pixExpiraEm || pixExpirado) return;

    const checar = () => {
      if (Date.now() >= pixExpiraEm) setPixExpirado(true);
    };
    checar();
    const id = setInterval(checar, 1000);
    return () => clearInterval(id);
  }, [status, pixExpiraEm, pixExpirado]);

  // Busca a conta detalhada do pedido já pago (resumo_do_pedido).
  useEffect(() => {
    if (status !== 'pago') return;
    let ativo = true;

    async function carregarConta() {
      try {
        const { data } = await supabase.rpc('resumo_do_pedido', {
          p_pedido_id: Number(pedidoId),
        });
        if (ativo && Array.isArray(data)) {
          setContaPaga(data as LinhaConta[]);
        }
      } catch {
        // Se der erro, apenas não mostra a caixinha.
      }
    }

    carregarConta();
    return () => {
      ativo = false;
    };
  }, [status, pedidoId]);

  // Pagamento confirmado: pede os avisos no celular (1,5 s depois), uma vez só.
  useEffect(() => {
    if (status !== 'pago' || avisosPedidosRef.current) return;
    avisosPedidosRef.current = true;
    solicitarAvisosAposSucesso(ingressos[0]?.evento);
  }, [status, ingressos]);

  async function copiarCodigo() {
    if (!pix) return;
    try {
      await navigator.clipboard.writeText(pix.pix_copia_e_cola);
      setCopiado(true);
      setTimeout(() => setCopiado(false), 2500);
    } catch {
      setPixErro('Não foi possível copiar. Selecione o código e copie manualmente.');
    }
  }

  const baixarIngresso = useCallback(async (ing: IngressoPedido) => {
    const qrCanvas = document.getElementById(
      `qr-${ing.inscricao_id}`,
    ) as HTMLCanvasElement | null;
    if (!qrCanvas) return;

    const nome = (ing.portador || 'ingresso').trim().replace(/\s+/g, '-').toLowerCase();

    // Desenho novo (padrão Se Liga). Se falhar, cai no desenho antigo logo abaixo.
    try {
      const canvas = await gerarConvite({
        evento: ing.evento,
        quando: formatarDataHora(ing.evento_em),
        local: ing.local_nome,
        rotulo: 'Ingresso',
        nome: ing.portador,
        detalhe: ing.portador_cpf ? `CPF ${mascaraCPF(ing.portador_cpf)}` : null,
        codigo: ing.ingresso,
      });
      await salvarOuCompartilhar(canvas, `ingresso-${nome}.png`, `Ingresso · ${ing.evento}`);
      return;
    } catch {
      // plano B: o desenho antigo (600×800), igual ao de hoje
    }

    // Mesmo desenho antigo da carteira e do convite da direção, com CPF e código curto.
    const canvas = gerarTicketIngresso({
      evento: ing.evento,
      dataHora: formatarDataHora(ing.evento_em),
      local: ing.local_nome,
      portador: ing.portador,
      participacao: '',
      cpf: ing.portador_cpf,
      codigo: ing.ingresso,
      qrCanvas,
    });

    const a = document.createElement('a');
    a.download = `ingresso-${nome}.png`;
    a.href = canvas.toDataURL('image/png');
    a.click();
  }, []);

  const whatsAppUrl = useMemo(() => {
    const ing = ingressos[0];
    if (!ing) return '#';
    const linhas = [
      `🎟️ Seu ingresso do ${ing.evento}`,
      `${formatarDataWhats(ing.evento_em)}${ing.local_nome ? ` · ${ing.local_nome}` : ''}`,
      `Portador: ${ing.portador}`,
      `Mostre este QR na entrada: ${window.location.href}`,
    ];
    return `https://wa.me/?text=${encodeURIComponent(linhas.join('\n'))}`;
  }, [ingressos]);

  function comprarDeNovo() {
    if (ingressos[0]?.evento_slug) {
      navigate(`/e/${ingressos[0].evento_slug}`);
    } else {
      navigate('/');
    }
  }

  async function enviarEmail() {
    setEnviandoEmail(true);
    setEmailErro(null);
    setEmailEnviadoPara(null);
    try {
      const { data, error } = await supabase.functions.invoke('enviar-ingresso-email', {
        body: { pedido_id: Number(pedidoId), email: emailDigitado.trim() || null },
      });
      if (error) {
        setEmailErro('Envio por e-mail em breve');
        return;
      }
      if (data?.ok) {
        const para =
          typeof data.enviado_para === 'string' && data.enviado_para
            ? data.enviado_para
            : emailDigitado.trim() || emailConta;
        setEmailEnviadoPara(para);
        setTimeout(() => setEmailEnviadoPara(null), 5000);
      } else {
        setEmailErro(mensagemAmigavel(data?.erro, 'Envio por e-mail em breve'));
      }
    } catch {
      setEmailErro('Envio por e-mail em breve');
    } finally {
      setEnviandoEmail(false);
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-background-100">
        <div className="mx-auto w-full max-w-[560px] px-4 py-8">
          <div className="h-8 w-56 animate-pulse rounded bg-secondary-100" />
          <div className="mt-6 h-40 w-full animate-pulse rounded-[18px] bg-secondary-100" />
          <div className="mt-4 h-64 w-full animate-pulse rounded-[18px] bg-secondary-100" />
        </div>
      </div>
    );
  }

  if (naoEncontrado) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background-100 px-4 py-8">
        <div className="w-full max-w-[420px] overflow-hidden rounded-[18px] border border-secondary-200 bg-white">
          <div className="p-6 text-center">
            <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-secondary-100 text-secondary-700">
              <i className="ri-search-line text-2xl" />
            </span>
            <h1 className="mt-4 text-xl font-medium text-foreground-950">
              Não encontramos este pedido.
            </h1>
            <button
              type="button"
              onClick={() => navigate('/meus-ingressos')}
              className="mt-6 w-full cursor-pointer whitespace-nowrap rounded-[14px] bg-primary-500 px-6 py-3 text-sm font-medium text-white transition-colors hover:bg-primary-600"
            >
              Ver meus ingressos
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (erro && !ingressos.length) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background-100 px-4 py-8">
        <div className="w-full max-w-[420px] overflow-hidden rounded-[18px] border border-secondary-200 bg-white">
          <div className="p-6 text-center">
            <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-secondary-100 text-secondary-700">
              <i className="ri-error-warning-line text-2xl" />
            </span>
            <h1 className="mt-4 text-xl font-medium text-foreground-950">
              Não foi possível carregar
            </h1>
            <p className="mt-2 text-sm text-foreground-600">{erro}</p>
            <button
              type="button"
              onClick={() => window.location.reload()}
              className="mt-6 w-full cursor-pointer whitespace-nowrap rounded-[14px] bg-primary-500 px-6 py-3 text-sm font-medium text-white transition-colors hover:bg-primary-600"
            >
              Tentar novamente
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ---- Aguardando confirmação do Pix ----
  if (status === 'aguardando') {
    return (
      <div className="min-h-screen bg-background-100">
        <div className="mx-auto w-full max-w-[560px] px-4 py-8">
          <div className="overflow-hidden rounded-[18px] border border-secondary-200 bg-white">
              <div className="p-6 sm:p-8">
              <div className="text-center">
                <h1 className="text-xl font-medium text-foreground-950">Falta só pagar</h1>
                {pix && (
                  <p className="mt-1 text-3xl font-bold text-foreground-950">
                    {totalDaConta(pix.detalhe, pix.total_reais)}
                  </p>
                )}
              </div>

              {pixErro && (
                <div className="mt-4">
                  <p className="rounded-[14px] border border-danger-200 bg-danger-50 px-4 py-3 text-sm text-danger-700">
                    {pixErro}
                  </p>
                  {!pix && (
                    <button
                      type="button"
                      onClick={gerarPix}
                      disabled={gerandoPix}
                      className="mt-3 w-full cursor-pointer whitespace-nowrap rounded-[14px] border border-secondary-300 px-6 py-3 text-sm font-medium text-foreground-800 transition-colors hover:bg-background-100 disabled:cursor-not-allowed disabled:bg-secondary-100 disabled:text-secondary-400"
                    >
                      {gerandoPix ? 'Gerando…' : 'Tentar de novo'}
                    </button>
                  )}
                </div>
              )}

              {!pix && !pixErro && (
                <div className="py-12 text-center">
                  <span className="mx-auto flex h-12 w-12 items-center justify-center text-primary-500">
                    <i className="ri-loader-4-line animate-spin text-4xl" />
                  </span>
                  <p className="mt-4 text-sm text-secondary-600">Gerando seu Pix…</p>
                </div>
              )}

              {pix && !pixExpirado && (
                <>
                  {pix.detalhe && pix.detalhe.length > 0 && (
                    <div className="mt-6">
                      <CaixaConta titulo="O QUE VOCÊ VAI PAGAR" linhas={pix.detalhe} />
                    </div>
                  )}

                  <div className="mt-6 flex justify-center">
                    <img
                      src={`data:image/png;base64,${pix.pix_qrcode_base64}`}
                      alt="QR Code Pix"
                      width={240}
                      height={240}
                      className="rounded-[18px] border border-secondary-200"
                    />
                  </div>

                  <p className="mt-4 text-center text-sm text-secondary-600">
                    Abra o app do seu banco, escolha Pix → Ler QR Code
                  </p>

                  <div className="my-5 flex items-center gap-3">
                    <div className="h-px flex-1 bg-secondary-200" />
                    <span className="text-xs text-secondary-400">ou</span>
                    <div className="h-px flex-1 bg-secondary-200" />
                  </div>

                  <div className="rounded-[18px] border border-secondary-200 bg-background-100 p-3">
                    <p className="max-h-24 overflow-y-auto break-all font-mono text-xs leading-relaxed text-secondary-600">
                      {pix.pix_copia_e_cola}
                    </p>
                  </div>

                  <button
                    type="button"
                    onClick={copiarCodigo}
                    className="mt-3 w-full cursor-pointer whitespace-nowrap rounded-[14px] border border-secondary-300 px-6 py-3 text-sm font-medium text-foreground-800 transition-colors hover:bg-background-100"
                  >
                    {copiado ? 'Copiado!' : 'Copiar código'}
                  </button>

                  <p className="mt-2 text-center text-xs text-secondary-500">
                    No celular: copie o código, abra o banco, Pix → Copia e Cola
                  </p>

                  <div className="mt-6 flex items-center justify-center gap-2 text-sm text-secondary-600">
                    <span className="flex h-4 w-4 items-center justify-center text-primary-500">
                      <i className="ri-loader-4-line animate-spin" />
                    </span>
                    Aguardando o pagamento…
                  </div>
                </>
              )}

              {pixExpirado && (
                <div className="mt-6 text-center">
                  <p className="text-base font-medium text-amber-700">Este Pix expirou</p>
                  <button
                    type="button"
                    onClick={gerarPix}
                    disabled={gerandoPix}
                    className="mt-4 w-full cursor-pointer whitespace-nowrap rounded-[14px] bg-primary-500 px-6 py-3 text-sm font-medium text-white transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:bg-secondary-200 disabled:text-secondary-400"
                  >
                    {gerandoPix ? 'Gerando…' : 'Gerar novo Pix'}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ---- Cancelado / expirado ----
  if (status === 'cancelado' || status === 'expirado') {
    return (
      <div className="min-h-screen bg-background-100">
        <div className="mx-auto w-full max-w-[560px] px-4 py-8">
          <div className="overflow-hidden rounded-[18px] border border-secondary-200 bg-white">
              <div className="p-8 text-center">
              <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-secondary-100 text-secondary-700">
                <i className="ri-close-line text-3xl" />
              </span>
              <h1 className="mt-4 text-xl font-medium text-foreground-950">
                Este pedido expirou.
              </h1>
              <p className="mt-2 text-sm text-secondary-600">
                O prazo de pagamento terminou e nenhum ingresso foi emitido.
              </p>
              <button
                type="button"
                onClick={comprarDeNovo}
                className="mt-6 w-full cursor-pointer whitespace-nowrap rounded-[14px] bg-primary-500 px-6 py-3 text-sm font-medium text-white transition-colors hover:bg-primary-600"
              >
                Comprar de novo
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ---- Pago ----
  const total = ingressos.length;

  return (
    <div className="min-h-screen bg-background-100">
      <div className="mx-auto w-full max-w-[560px] px-4 py-8">
        <div className="text-center">
          <span className="check-pop mx-auto flex h-20 w-20 items-center justify-center rounded-full bg-accent-500 text-white">
            <i className="ri-check-line text-5xl" aria-hidden />
          </span>
          <h1 className="mt-5 text-[22px] font-extrabold text-foreground-950">
            Pagamento confirmado!
          </h1>
          <p className="mt-2 text-sm text-secondary-600">
            {total === 1 ? 'Seu ingresso está pronto.' : `Seus ${total} ingressos estão prontos.`}
          </p>
        </div>

        <div className="mt-6 space-y-4">
          {ingressos.map((ing) => (
            <div key={ing.inscricao_id} className="space-y-3">
              <CartaoIngresso ingresso={ing} />
              <button
                type="button"
                onClick={() => baixarIngresso(ing)}
                className="flex w-full cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-[14px] bg-primary-500 px-6 py-3 text-sm font-medium text-white transition-colors hover:bg-primary-600"
              >
                <span className="flex h-4 w-4 items-center justify-center">
                  <i className="ri-download-2-line" />
                </span>
                Baixar ingresso
              </button>
            </div>
          ))}
        </div>

        {contaPaga.length > 0 && (
          <div className="mt-6">
            <CaixaConta titulo="O QUE VOCÊ PAGOU" linhas={contaPaga} />
          </div>
        )}

        <div className="mt-6 flex flex-col gap-3 sm:flex-row">
          <a
            href={whatsAppUrl}
            target="_blank"
            rel="noreferrer nofollow"
            className="flex flex-1 cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-[14px] border border-secondary-300 px-6 py-3 text-sm font-medium text-foreground-800 transition-colors hover:bg-background-100"
          >
            <span className="flex h-4 w-4 items-center justify-center text-primary-600">
              <i className="ri-whatsapp-line" />
            </span>
            Enviar no WhatsApp
          </a>
          <button
            type="button"
            onClick={() => setEmailAberto((v) => !v)}
            className="flex flex-1 cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-[14px] border border-secondary-300 px-6 py-3 text-sm font-medium text-foreground-800 transition-colors hover:bg-background-100"
          >
            <span className="flex h-4 w-4 items-center justify-center text-primary-600">
              <i className="ri-mail-line" />
            </span>
            Receber por e-mail
          </button>
        </div>

        {emailAberto && (
          <div className="mt-3 rounded-[14px] border border-secondary-200 bg-white p-4">
            <label htmlFor="email-ingresso" className="text-sm font-medium text-foreground-800">
              Enviar para:
            </label>
            <input
              id="email-ingresso"
              type="email"
              value={emailDigitado}
              onChange={(e) => setEmailDigitado(e.target.value)}
              placeholder="seu@email.com"
              className="mt-1 w-full rounded-[14px] border border-secondary-300 bg-white px-3 py-2 text-sm text-foreground-900 outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-100"
            />
            {emailErro && <p className="mt-2 text-sm text-danger-700">{emailErro}</p>}
            <button
              type="button"
              onClick={enviarEmail}
              disabled={enviandoEmail}
              className="mt-3 flex w-full cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-[14px] bg-primary-500 px-6 py-3 text-sm font-medium text-white transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:bg-secondary-300"
            >
              {enviandoEmail
                ? 'Enviando…'
                : emailEnviadoPara
                  ? `✓ Enviado para ${emailEnviadoPara}`
                  : 'Enviar'}
            </button>
          </div>
        )}

        <p className="mt-6 text-center text-xs text-secondary-500">
          Mostre o QR na entrada do evento. Na portaria, aumente o brilho da tela para facilitar a
          leitura.
        </p>
      </div>
    </div>
  );
}